import { type DragEventHandler, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import type { FileType, SelectedFile } from '@witmetrics/utils';
import { isEmpty } from '@witmetrics/utils/misc';
import { useToggle } from '@/hooks/useToggle';
import { useMountEffect } from '@/hooks/useMountEffect';
import FileInput, { type FileInputProps } from '@/components/inputs/FileInput';
import Card from '@/components/Card';
import EmptySelector from './EmptySelector';
import FileSelection from './FileSelection';
import { classes, styles } from './utils';

export type FileSelectorProps = {
  multiple?: FileInputProps['multiple'];
  className?: string;
  fileType: FileType | string;
  accept?: FileInputProps['accept'];
  title: string;
  subtitle: string;
  selectedFiles: SelectedFile[];
  onAddFiles: (files: File[]) => void;
  onRemoveFiles: (uuids: string[]) => void;
};

export default function FileSelector({
  multiple = true,
  className,
  fileType,
  accept,
  title,
  subtitle,
  selectedFiles,
  onAddFiles,
  onRemoveFiles,
}: FileSelectorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, toggleDraggingOver] = useToggle();

  useMountEffect(() => {
    window.addEventListener('dragend', cancelDragging);
    return () => {
      window.removeEventListener('dragend', cancelDragging);
    };
  });

  const cancelDragging = () => {
    toggleDraggingOver(false);
  };

  const handleDrop: DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    cancelDragging();
    onAddFiles(Array.from(e.dataTransfer.files));
  };

  const handleRemoveFile = (selectedFile: SelectedFile) => {
    if (inputRef.current && !multiple) inputRef.current.value = '';
    // NOTE: Leaving menu open so user doesn't have to reopen to remove multiple files
    onRemoveFiles([selectedFile.uuid]);
  };

  return (
    <>
      <FileInput
        ref={inputRef}
        multiple={multiple}
        className={classes.input}
        id={fileType}
        accept={accept}
        onSelect={onAddFiles}
      />
      <label htmlFor={fileType}>
        <Card
          sx={styles.card}
          className={twMerge(
            classes.card,
            isEmpty(selectedFiles) ? classes.empty : classes.filled,
            className
          )}
          onDragEnter={() => toggleDraggingOver(true)}
          onDragLeave={cancelDragging}
          onDrop={handleDrop}>
          {isEmpty(selectedFiles) ? (
            <EmptySelector title={title} subtitle={subtitle} />
          ) : (
            <FileSelection
              title={title}
              subtitle={subtitle}
              selectedFile={selectedFiles[0]}
              onRemove={handleRemoveFile}
            />
          )}
        </Card>
      </label>
    </>
  );
}
